import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './MagicLinkCallback.css';
import Logo from '../../../components/Logo/Logo';

import { applyHandoffContinuation } from '../../edu-landing/handoffContinuation';
import { continuePendingCheckoutOrChat } from '../continueAfterSignIn';
import { withAppBasename } from '../../../config/appBase';
import {
  clearPasswordChangeSignIn,
  recordPasswordChangeSignIn,
} from '../passwordChangeSignIn';

const MagicLinkCallback = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { loginWithMagicToken } = useAuth();
  
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Initializing verification...');
  const [isVisible, setIsVisible] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    // A new loginWithMagicToken from AuthProvider must not send the token
    // again. The server rejects the second use and the page shows
    // "Verification Failed" while the first sign-in still succeeds.
    if (startedRef.current) return;
    startedRef.current = true;

    setIsVisible(true);
    
    const verifyToken = async () => {
      if (!token) {
        setError('No magic login token was provided in the link. Please try logging in again.');
        return;
      }
      
      try {
        setStatus('Verifying your magic login link...');
        recordPasswordChangeSignIn({ source: 'magic' });
        const result = await loginWithMagicToken(token);
        
        if (result.success) {
          applyHandoffContinuation(result);
          if (result.user?.must_change_password) {
            setStatus('Login successful! Redirecting you...');
            navigate('/set-password', { replace: true });
            return;
          }
          clearPasswordChangeSignIn();
          setStatus('Login successful! Redirecting you...');
          setTimeout(() => {
            const returnTo = searchParams.get('returnTo');
            if (returnTo && returnTo.startsWith('/')) {
              navigate(returnTo, { replace: true });
              return;
            }
            continuePendingCheckoutOrChat({ navigate, user: result.user });
          }, 800);
        } else {
          clearPasswordChangeSignIn();
          setError(result.error || 'The magic link verification failed. It might have expired or been used already.');
        }
      } catch (err) {
        clearPasswordChangeSignIn();
        console.error('Magic link auth error:', err);
        setError('An unexpected error occurred during verification. Please try again.');
      }
    };

    verifyToken();
  }, [token, loginWithMagicToken, navigate]);

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-overlay"></div>
      </div>
      
      <div className="login-container">
        <div className={`login-content ${isVisible ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', padding: '50px 40px' }}>
          <div className="login-header" style={{ justifyContent: 'center', marginBottom: '24px' }}>
            <div className="login-logo">
              <a
                href={withAppBasename('/')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                style={{
                  textDecoration: 'none',
                  fontFamily: 'var(--iv-font-head)',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: 'var(--iv-head)',
                  letterSpacing: '-0.02em',
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: '6px'
                }}
              >
                IVORI <span style={{ fontFamily: 'var(--iv-font-body)', fontSize: '15px', fontWeight: 600, color: 'var(--iv-body)' }}>Edu</span>
              </a>
            </div>
          </div>
          
          <div className="callback-card-content">
            {error ? (
              <div className="error-state">
                <div className="error-icon" style={{ 
                  color: 'var(--iv-danger)', 
                  fontSize: '36px', 
                  marginBottom: '16px' 
                }}>
                  ⚠
                </div>
                <h2 style={{ fontFamily: 'var(--iv-font-head)', color: 'var(--iv-body)', marginBottom: '10px', fontSize: '22px', fontWeight: '700' }}>
                  Verification Failed
                </h2>
                <p style={{ color: 'var(--iv-muted)', marginBottom: '28px', fontSize: '14px', lineHeight: '1.5' }}>
                  {error}
                </p>
                <button 
                  onClick={() => navigate('/login')}
                  style={{
                    width: '100%',
                    backgroundColor: 'var(--iv-btn)',
                    color: 'var(--iv-btn-fg)',
                    border: '1px solid var(--iv-btn)',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontFamily: 'var(--iv-font-body)'
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <div className="loading-state">
                <div className="premium-spinner-container">
                  <div className="outer-pulse-ring"></div>
                  <div className="inner-rotating-ring"></div>
                </div>
                <h3 style={{ fontFamily: 'var(--iv-font-head)', color: 'var(--iv-head)', fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                  Authenticating
                </h3>
                <p style={{ color: 'var(--iv-muted)', fontSize: '14px' }}>
                  {status}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicLinkCallback;
